import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "frame", "searchLink"];
  static values = { url: String };

  connect() {
    this.onFormChange = this.onFormChange.bind(this);
    this.form = this.element.closest("form");
    this.form?.addEventListener("change", this.onFormChange);
    this.updateSearchLink();
  }

  disconnect() {
    clearTimeout(this.timeout);
    this.form?.removeEventListener("change", this.onFormChange);
  }

  // A number being typed may be unfinished, so show a match as soon as there
  // is one, but leave "not found" until the input is left.
  search() {
    this.clear();
    this.timeout = setTimeout(() => this.#load({ incomplete: true }), 300);
  }

  // Bound to blur as well as change: change only fires when the value differs
  // from when the input was focused, so retyping the same number fires none.
  load() {
    clearTimeout(this.timeout);
    this.#load({ incomplete: false });
  }

  clear() {
    clearTimeout(this.timeout);
    this.frameTarget.removeAttribute("src");
    this.frameTarget.replaceChildren();
  }

  // The utility picked at the top of the questionnaire decides which program's
  // eligibility this row reflects. When it changes, re-point the search link and
  // re-render the entered unit's badges under the new program.
  onFormChange(event) {
    if (event.target?.name !== "questionnaire[electric_utility]") return;

    this.updateSearchLink();
    if (!this.inputTarget.value.trim()) return;

    this.clear();
    this.#load({ incomplete: false });
  }

  updateSearchLink() {
    if (!this.hasSearchLinkTarget) return;

    const url = new URL(this.searchLinkTarget.href, document.baseURI);
    const utility = this.#electricUtility();
    if (utility) url.searchParams.set("electric_utility", utility);
    else url.searchParams.delete("electric_utility");
    this.searchLinkTarget.href = url.href;
  }

  #load({ incomplete }) {
    const number = this.inputTarget.value.trim();
    if (!number || this.#shows(number)) return;

    const url = new URL(
      this.urlValue.replace("AHRI_REFERENCE_NUMBER", encodeURIComponent(number)),
      document.baseURI
    );
    if (incomplete) url.searchParams.set("incomplete", "true");

    const utility = this.#electricUtility();
    if (utility) url.searchParams.set("electric_utility", utility);
    if (this.frameTarget.getAttribute("src") === url.href) return;

    this.frameTarget.src = url.href;
  }

  #electricUtility() {
    return (
      this.form?.querySelector('input[name="questionnaire[electric_utility]"]:checked')?.value || ""
    );
  }

  #shows(number) {
    return this.frameTarget.querySelector(`[data-ahri-reference-number="${CSS.escape(number)}"]`);
  }
}
